import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { deckSubmissionService } from '../../services/deckSubmissionService';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import StatusNotificationBadge from '../../components/ui/StatusNotificationBadge';
import AdminQueueIndicator from '../../components/ui/AdminQueueIndicator';
import MetricsCard from './components/MetricsCard';
import SystemNotificationPanel from './components/SystemNotificationPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import InternalUserManagement from './components/InternalUserManagement';
import CustomerUserManagement from './components/CustomerUserManagement';
import AnnouncementManagementPanel from './components/AnnouncementManagementPanel';
import WebsiteUserManagement from './components/WebsiteUserManagement';

const gradientStyle = {
  background: 'linear-gradient(135deg, #1a56db 0%, #3b82f6 50%, #1d4ed8 100%)',
  border: 'none',
  color: '#ffffff',
};

const GradientButton = ({ children, onClick, iconName, size, className = '', disabled = false, ...props }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg ${className}`}
    style={gradientStyle}
    {...props}
  >
    {iconName && <Icon name={iconName} size={size === 'sm' ? 14 : 16} color="#ffffff" />}
    {children}
  </button>
);

const GradientButtonOutline = ({ children, onClick, iconName, size, className = '', disabled = false, ...props }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg ${className}`}
    style={gradientStyle}
    {...props}
  >
    {iconName && <Icon name={iconName} size={size === 'sm' ? 14 : 16} color="#ffffff" />}
    {children}
  </button>
);

const AdminConsole = () => {
  const navigate = useNavigate();
  const { getClientForRole } = useAuth();

  const [queueItems, setQueueItems] = useState([]);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [refreshContent, setRefreshContent] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    pending: 0,
    completed: 0,
    total: 0,
    highPriority: 0
  });
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [avgProcessingTime, setAvgProcessingTime] = useState('0h');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSubmissions();
    loadActiveUsers();
    loadProcessingMetrics();
  }, []);

  useEffect(() => {
    const adminClient = getClientForRole('admin');
    const channel = adminClient
      ?.channel('admin_console_changes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'deck_submissions' }, (payload) => {
        console.log('Deck submission change detected:', payload);
        loadSubmissions();
        loadActiveUsers();
        loadProcessingMetrics();
      })
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'audit_reports' }, (payload) => {
        console.log('Audit report change detected:', payload);
        loadSubmissions();
        loadProcessingMetrics();
      })
      ?.subscribe();

    return () => { adminClient?.removeChannel(channel); };
  }, [getClientForRole]);

  const loadActiveUsers = async () => {
    try {
      const adminClient = getClientForRole('admin');
      if (!adminClient) return;
      const sevenDaysAgo = new Date();
      sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);
      const { data, error } = await adminClient?.from('deck_submissions')?.select('user_id')?.gte('submitted_at', sevenDaysAgo?.toISOString());
      if (error) throw error;
      const uniqueUsers = new Set(data?.map(sub => sub?.user_id));
      setActiveUsersCount(uniqueUsers?.size);
    } catch (error) {
      console.error('Error loading active users:', error);
      setActiveUsersCount(0);
    }
  };

  const loadProcessingMetrics = async () => {
    try {
      const adminClient = getClientForRole('admin');
      if (!adminClient) return;
      const { data, error } = await adminClient?.from('deck_submissions')?.select(`submitted_at, audit_reports (published_at)`)?.not('audit_reports.published_at', 'is', null);
      if (error) throw error;
      if (!data || data?.length === 0) { setAvgProcessingTime('0h'); return; }
      let totalHours = 0;
      let validCount = 0;
      data?.forEach(submission => {
        if (submission?.audit_reports && submission?.audit_reports?.length > 0) {
          const submittedAt = new Date(submission?.submitted_at);
          const publishedAt = new Date(submission?.audit_reports?.[0]?.published_at);
          const diffHours = (publishedAt - submittedAt) / (1000 * 60 * 60);
          if (diffHours > 0) { totalHours += diffHours; validCount++; }
        }
      });
      setAvgProcessingTime(validCount > 0 ? `${Math.round(totalHours / validCount)}h` : '0h');
    } catch (error) {
      console.error('Error loading processing metrics:', error);
      setAvgProcessingTime('N/A');
    }
  };

  const loadSubmissions = async () => {
    try {
      setIsLoading(true);
      const [submissions, submissionStats] = await Promise.all([
        deckSubmissionService?.getAllSubmissions(),
        deckSubmissionService?.getSubmissionStats()
      ]);
      const transformedQueue = submissions?.map(sub => ({
        id: sub?.id,
        deckName: sub?.title,
        userEmail: sub?.user_profiles?.email || 'Unknown',
        priority: sub?.priority,
        submittedAt: sub?.submitted_at,
        fileType: sub?.file_type,
        slideCount: sub?.slide_count,
        assignedAnalyst: sub?.assigned_analyst,
        assignedAnalystId: sub?.assigned_analyst_id,
        status: sub?.status
      })) || [];
      setQueueItems(transformedQueue);
      setStats(submissionStats);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserAction = (action, userId) => {
    console.log(`User action: ${action} for user ${userId}`);
  };

  const handleBulkAction = (action, userIds) => {
    console.log(`Bulk action: ${action} for users:`, userIds);
  };

  const handleAssignAnalyst = async (itemId, analystId) => {
    try {
      const analystNames = {
        analyst1: "Sarah Johnson",
        analyst2: "Michael Chen",
        analyst3: "Emily Rodriguez",
        analyst4: "David Kim"
      };
      await deckSubmissionService?.assignAnalyst(itemId, analystId, analystNames?.[analystId]);
      setQueueItems(queueItems?.map(item =>
        item?.id === itemId ? { ...item, assignedAnalystId: analystId, assignedAnalyst: analystNames?.[analystId] } : item
      ));
    } catch (error) {
      console.error('Error assigning analyst:', error);
    }
  };

  const handleChangePriority = async (itemId, priority) => {
    try {
      await deckSubmissionService?.updateSubmissionPriority(itemId, priority);
      setQueueItems(queueItems?.map(item => item?.id === itemId ? { ...item, priority } : item));
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const handleViewDetails = (itemId) => console.log('Viewing audit details:', itemId);

  const handleSendNotification = (notificationData) => {
    console.log('Sending notification:', notificationData);
    setShowNotificationPanel(false);
  };

  const handleExportData = () => console.log('Exporting user data...');

  const pendingAuditsCount = stats?.pending || 0;
  const estimatedProcessingTime = pendingAuditsCount * 90;

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader userRole="admin" pendingAudits={pendingAuditsCount} />
      <main className="pt-[64px] md:pt-[64px]">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">

          {/* ── Header ── */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Admin Console
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Manage users, monitor audits, and oversee platform operations
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <AdminQueueIndicator
                queueCount={pendingAuditsCount}
                estimatedTime={estimatedProcessingTime}
              />
              <StatusNotificationBadge
                count={3}
                type="warning"
                label="Alerts"
                onClick={() => console.log('View alerts')}
              />
              <GradientButtonOutline iconName="Download" onClick={handleExportData}>
                Export Data
              </GradientButtonOutline>
              <GradientButton iconName="FileUp" onClick={() => navigate('/admin-audit-management')}>
                Audit Management
              </GradientButton>
            </div>
          </div>

          {/* ── Metrics ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <MetricsCard icon="ClipboardList" title="Pending Audits" value={stats?.pending} change="+12%" changeType="positive" description="Awaiting review" />
            <MetricsCard icon="CheckCircle2" title="Completed" value={stats?.completed} change="+8%" changeType="positive" description="This month" />
            <MetricsCard icon="Users" title="Active Users" value={activeUsersCount} change="+5%" changeType="positive" description="Last 7 days" />
            <MetricsCard icon="Clock" title="Avg. Processing" value={avgProcessingTime} change="-2h" changeType="positive" description="Per audit" />
          </div>

          {/* ── User Management ── */}
          <div className="mb-6 md:mb-8">
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-4 mb-6 border border-primary/20">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/20">
                  <Icon name="Shield" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">User Management Sections</h3>
                  <p className="text-sm text-muted-foreground">Segregated management for internal and customer users</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:gap-8">
              <InternalUserManagement />
              <CustomerUserManagement
                onUserAction={handleUserAction}
                onBulkAction={handleBulkAction}
              />
              <WebsiteUserManagement
                onUserAction={handleUserAction}
                onBulkAction={handleBulkAction}
              />
            </div>
          </div>

          {/* ── System Communications ── */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg md:text-xl font-semibold text-foreground">
                System Communications
              </h2>
              <GradientButtonOutline
                size="sm"
                iconName={showNotificationPanel ? "ChevronUp" : "ChevronDown"}
                onClick={() => setShowNotificationPanel(!showNotificationPanel)}
              >
                {showNotificationPanel ? 'Hide' : 'Show'} Panel
              </GradientButtonOutline>
            </div>

            {showNotificationPanel && (
              <SystemNotificationPanel onSendNotification={handleSendNotification} />
            )}
          </div>

          {/* ── Announcements ── */}
          <div className="mt-6 md:mt-8">
            <AnnouncementManagementPanel />
          </div>

        </div>
      </main>

      <footer className="bg-card border-t border-border mt-12">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {new Date()?.getFullYear()} myDeckAudit. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <button className="text-sm text-muted-foreground hover:text-foreground transition-smooth">Privacy Policy</button>
              <button className="text-sm text-muted-foreground hover:text-foreground transition-smooth">Terms of Service</button>
              <button className="text-sm text-muted-foreground hover:text-foreground transition-smooth">Contact Support</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AdminConsole;